import { Link, useParams, Navigate } from "react-router-dom";
import SEO, { breadcrumbSchema, faqSchema, locationSchema, orgSchema } from "../../lib/seo";
import { SUBURBS, suburbBySlug } from "../../data/suburbs";
import { LOCATIONS, IMG, trackPhoneClick } from "../../data/site";
import { programBySlug } from "../../data/programs";
import { REVIEWS } from "../../data/content";
import { Reveal, Eyebrow, TrialButton, GhostButton, SectionShell, FAQBlock } from "../../components/shared";
import { Star } from "lucide-react";

export default function NearPage() {
  const { slug } = useParams();
  const s = suburbBySlug(slug);
  if (!s) return <Navigate to="/locations" replace />;
  const locs = s.nearest.map((l) => LOCATIONS.find((x) => x.slug === l));
  const primary = locs[0];
  const locReviews = REVIEWS.filter((r) => r.source.includes(primary.short));
  const review = locReviews[SUBURBS.findIndex((x) => x.slug === slug) % locReviews.length];
  return (
    <>
      <SEO
        title={`Martial Arts Classes Near ${s.city}, TX | Mezger Martial Arts at Hidden Gym`}
        description={`Muay Thai, boxing, BJJ, MMA & kids martial arts near ${s.city}, TX — coached by Guy Mezger's staff at Hidden Gym. ${s.drive} First class free.`}
        schemas={[orgSchema(), ...locs.map(locationSchema), faqSchema(s.faqs), breadcrumbSchema([["Home", "/"], ["Locations", "/locations"], [`Near ${s.city}`, `/near/${s.slug}`]])]}
      />
      <section className="relative bg-[#0A0B0D] text-white pt-24 pb-16 overflow-hidden grain">
        <img src={IMG.gymAction1} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D]/70 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Eyebrow>Serving {s.city}, TX</Eyebrow>
            <h1 className="mt-3 font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95]" data-testid="near-page-title">
              Martial Arts Classes<br /><span className="text-neutral-400">Near {s.city}</span>
            </h1>
            <p className="mt-5 max-w-2xl text-white/75">{s.intro}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <TrialButton campaign={`near-${s.slug}`} page={`near-${s.slug}`} location={primary.slug} testId="near-trial-btn">Book a Free Trial Class</TrialButton>
              <GhostButton to="/schedule" className="text-white border-white/40" testId="near-schedule-btn">See the Schedule</GhostButton>
            </div>
          </Reveal>
        </div>
      </section>

      <SectionShell className="bg-white">
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-6">
            <Reveal>
              <h2 className="font-display font-black uppercase text-2xl sm:text-3xl">Your Closest Mats</h2>
              <p className="mt-3 text-[#374151] leading-relaxed">{s.drive}</p>
              {s.body.map((p, i) => <p key={i} className="mt-4 text-[#374151] leading-relaxed">{p}</p>)}
            </Reveal>
            <Reveal>
              <h2 className="font-display font-black uppercase text-2xl sm:text-3xl mt-6">{s.city} Questions</h2>
              <div className="mt-2"><FAQBlock faqs={s.faqs} testPrefix={`near-faq-${s.slug}`} /></div>
            </Reveal>
          </div>
          <aside className="space-y-6">
            {locs.map((l) => (
              <Reveal key={l.slug}>
                <div className="border border-black/10">
                  <iframe title={`Map of ${l.name}`} src={l.mapEmbed} className="w-full h-48 border-0" loading="lazy" allowFullScreen />
                  <div className="p-5 text-sm">
                    <h3 className="font-display font-black uppercase text-lg">{l.short}</h3>
                    <p className="mt-1 text-[#374151]">{l.address}</p>
                    <p className="text-[#374151]"><a href={l.phoneHref} onClick={() => trackPhoneClick(l.slug, l.phoneHref)} className="hover:text-neutral-500 font-medium" data-testid={`near-phone-${l.slug}`}>{l.phone}</a></p>
                    <p className="text-[#5A6270]">{l.hours}</p>
                    <Link to={`/locations/${l.slug}`} className="mt-3 inline-block underline font-bold hover:text-neutral-500" data-testid={`near-location-link-${l.slug}`}>Location details →</Link>
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal>
              <div className="bg-[#0A0B0D] text-white p-6">
                <h3 className="font-display font-black uppercase text-lg">Programs at {primary.short}</h3>
                <ul className="mt-3 space-y-1.5 text-sm">
                  {primary.programs.map((p) => (
                    <li key={p}><Link to={`/programs/${p}`} className="text-white/75 hover:text-white underline">{programBySlug(p).name}</Link></li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </aside>
        </div>
      </SectionShell>

      {review && (
        <SectionShell dark className="grain">
          <Reveal>
            <Eyebrow>From the Mats at {primary.short}</Eyebrow>
            <figure className="mt-6 max-w-3xl" data-testid={`near-review-${s.slug}`}>
              <div className="flex text-white">{Array.from({ length: review.stars }).map((_, i) => <Star key={i} size={18} fill="currentColor" />)}</div>
              <blockquote className="mt-5 font-display font-bold text-2xl sm:text-3xl leading-snug">"{review.text}"</blockquote>
              <figcaption className="mt-5 font-mono text-xs uppercase tracking-widest text-white/50">{review.program} · {review.source} — {primary.short} is {s.city}'s closest location</figcaption>
            </figure>
          </Reveal>
        </SectionShell>
      )}

      <SectionShell>
        <Reveal>
          <Eyebrow>Also Nearby</Eyebrow>
          <div className="mt-3 flex flex-wrap gap-2">
            {SUBURBS.filter((x) => x.slug !== slug).map((x) => (
              <Link key={x.slug} to={`/near/${x.slug}`} data-testid={`near-related-${x.slug}`}
                className="border border-black/15 px-4 py-2 text-sm font-bold uppercase tracking-wide hover:border-[#0A0B0D] hover:text-[#0A0B0D] transition-colors">
                Near {x.city}
              </Link>
            ))}
          </div>
        </Reveal>
      </SectionShell>
    </>
  );
}
